<template>
  <VCard
    v-if="isJoiningCall && !isInCall"
    class="mt-4 max-w-[1200px]"
    data-testid="call-frame-loader"
  >
    <PreCallSkeleton />
  </VCard>

  <VCard v-if="roomId && !isCallFound" class="mt-4 p-4 flex justify-center">
    <LoaderElement type="circular" size="large" />
  </VCard>

  <VCard v-if="hasCallFrame" class="mt-4 max-w-[1200px]">
    <WebexConsultation
      v-if="webexCallAccess"
      :key="webexCallAccess.consultationRequestId"
      :guest-token="webexCallAccess.guestToken"
      :meeting-destination="webexCallAccess.webLink"
      :password="webexCallAccess.password"
      :invite-link="inviteLink"
      @ended="handleCallClosed"
    />
    <iframe
      v-else-if="callFrameSrc"
      :src="callFrameSrc"
      allow="camera *; microphone *; display-capture *"
    />

    <CallFrameDetails
      v-if="isInScheduledCall && scheduledConsultationDTO"
      :cases="cases"
      :custom-fields="scheduledConsultationDTO.customFields"
      :details="{
        type: 'scheduled',
        comment: scheduledConsultationDTO.patientPresentationComment,
        medicalHistory: scheduledConsultationDTO.patientPresentationMedicalHistory,
        conferenceTitle: scheduledConsultationDTO.conferenceTopic,
        conferenceDescription: scheduledConsultationDTO.conferenceDescription
      }"
      v-model:show-details="showDetails"
    />

    <CallFrameDetails
      v-if="isInDirectCall && directConsultation"
      :cases="[directConsultation.case]"
      :custom-fields="directConsultation.customFields"
      :details="{
        type: 'direct',
        diagnosis: directConsultation.diagnosis,
        question: directConsultation.question,
        medicalHistory: directConsultation.medicalHistory,
        therapy: directConsultation.therapy
      }"
      v-model:show-details="showDetails"
    />

    <VCard
      class="flex flex-col md:flex-row gap-2 items-center flex-wrap justify-between flex-1 ml-1 p-4 pt-2"
      flat
    >
      <template v-if="isInDirectCall && !isJoinConsultationPath && consultationShareCode">
        <p class="text-h6">{{ t('Invite more participants:') }}</p>
        <div class="flex items-center gap-2 border border-gray-300 max-w-full">
          <VTextField
            v-model="consultationShareCode"
            disabled
            hide-details="auto"
            variant="solo"
            :style="{ width: `${consultationShareCode.length}ch` }"
          />
          <VBtn variant="plain" @click="copyShareCodeToClipboard">
            <VIcon icon="content_copy" class="text-secondary-text" />
          </VBtn>
        </div>
      </template>

      <template
        v-if="isInScheduledCall && !isJoinConsultationPath && scheduledConsultationShareCode"
      >
        <p class="text-h6">{{ t('Invite more participants:') }}</p>
        <div class="flex items-center gap-2 border border-gray-300 max-w-full">
          <VTextField
            v-model="scheduledConsultationShareCode"
            disabled
            hide-details="auto"
            variant="solo"
            :style="{ width: `${scheduledConsultationShareCode.length}ch` }"
          />
          <VBtn variant="plain" @click="copyShareCodeToClipboard">
            <VIcon icon="content_copy" class="text-secondary-text" />
          </VBtn>
        </div>
      </template>

      <template v-if="isInScheduledCall && scheduledConsultationDTO">
        <div class="flex items-center gap-1 max-w-full">
          <span class="label text-h6">
            {{ translateScheduledConsultationTypeDTO(scheduledConsultationDTO.type, t) }}:
          </span>
          <span class="value"
            >{{ formatDateTime(scheduledConsultationDTO.scheduledStartTime) }}
            {{ t("o'clock") }}</span
          >
        </div>
      </template>

      <VBtn
        :text="t('Legal Notice')"
        :href="legalNoticeUrl"
        variant="plain"
        :ripple="false"
        target="_blank"
        class="text-body-2 p-0 ml-auto"
      />
    </VCard>
  </VCard>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, onUnmounted, ref, watch } from 'vue'
import { useCallStore } from '@/stores/call'
import { storeToRefs } from 'pinia'
import { getAkBaseUrl } from '@/global/environmentGetters'
import { useNoticeStore } from '@/stores/notice'
import { NoticeType } from '@/stores/models/Notice'
import { useRoute, useRouter } from 'vue-router'
import { routeNames } from '@/router/routeNames'
import { useI18n } from 'vue-i18n'
import LoaderElement from '@/atoms/LoaderElement.vue'
import PreCallSkeleton from '@/components/webexCall/PreCallSkeleton.vue'
import {
  useConsultationActivity,
  type ConsultationType
} from '@/composables/useConsultationActivity'
import { useRefreshGuard } from '@/composables/useRefreshGuard'
import CallFrameDetails from '@/atoms/CallFrameDetails.vue'
import { handleScrollToTopWhenInCall } from '@/util/callFrame.utils'
import { useFormatDate } from '@/composables/useFormatDate'
import { translateScheduledConsultationTypeDTO } from '@/util/translations'

const WebexConsultation = defineAsyncComponent({
  loader: () => import('@/components/webexCall/WebexConsultation.vue'),
  loadingComponent: PreCallSkeleton
})

const { t, locale } = useI18n()
const { formatDateTime } = useFormatDate()
const route = useRoute()
const router = useRouter()

const {
  callConsultationRequestId,
  isInCall,
  isInDirectCall,
  isInScheduledCall,
  isJoiningCall,
  isWebexCall,
  webexCallAccess,
  roomId,
  accessToken,
  consultationShareCode,
  scheduledConsultationShareCode,
  scheduledConsultation,
  directConsultationRequest
} = storeToRefs(useCallStore())

const {
  joinCallWithConsultationShareCode,
  getConsultationShareCode,
  getScheduledConsultationShareCode,
  leaveCall,
  tryRestoreActiveCall
} = useCallStore()

const { add: addNotice } = useNoticeStore()

const showDetails = ref(false)
const isCallFound = ref(false)
const callFrameSrc = ref<string | null>(null)

const isJoinConsultationPath = computed(() => route.name === 'join-consultation')
const isScheduledCallPath = computed(() => route.name === routeNames.SCHEDULED_CALL)
const isSharedCodePresent = computed(() => route.query.shareCode !== undefined)

const showCard = computed<boolean>(() => isInCall.value)
// The AK iframe is Arztkonsultation's service; a Webex call is run by e:lmo itself.
const legalNoticeUrl = computed(() =>
  isWebexCall.value ? 'https://tcc-ai.de/en/imprint' : 'https://arztkonsultation.de/impressum/'
)
const hasCallFrame = computed<boolean>(
  () => showCard.value && (isWebexCall.value || !!callFrameSrc.value)
)
const scheduledConsultationDTO = computed(() => scheduledConsultation.value)
const directConsultation = computed(() => directConsultationRequest.value)
// Same link the card below the call offers; guests who joined through a link get none.
const inviteLink = computed(() => {
  if (isJoinConsultationPath.value) return undefined
  if (isInDirectCall.value) return consultationShareCode.value
  if (isInScheduledCall.value) return scheduledConsultationShareCode.value
  return undefined
})
const cases = computed(() => scheduledConsultation.value?.cases ?? [])

onMounted(() => window.addEventListener('message', handleMessageEvent))
onUnmounted(() => window.removeEventListener('message', handleMessageEvent))
useRefreshGuard(() => hasCallFrame.value)

watch(
  () => locale.value,
  async () => {
    // Re-joining would tear down a live Webex meeting; only the AK iframe URL carries the locale.
    if (isWebexCall.value) return
    await tryRestoreActiveCall()
    callFrameSrc.value = createCallFrameUrl()
  }
)

watch(
  callConsultationRequestId,
  async () => {
    if (callConsultationRequestId.value && isInDirectCall.value && !isJoinConsultationPath.value) {
      await getConsultationShareCode(callConsultationRequestId.value)
    }
    if (
      callConsultationRequestId.value &&
      isInScheduledCall.value &&
      !isJoinConsultationPath.value &&
      !isSharedCodePresent.value
    ) {
      await getScheduledConsultationShareCode(callConsultationRequestId.value)
    }

    callFrameSrc.value = createCallFrameUrl()
  },
  { immediate: true }
)

const currentConsultationType = computed<ConsultationType>(() =>
  isInScheduledCall.value ? 'SCHEDULED' : 'DIRECT'
)

useConsultationActivity(isCallFound, currentConsultationType)

watch([showCard, callFrameSrc, isWebexCall], () => {
  if (hasCallFrame.value) {
    handleScrollToTopWhenInCall()

    if (isInScheduledCall.value || isInDirectCall.value) {
      isCallFound.value = true
    }
  }
})

watch(
  () => route.params.consultationShareCode,
  (newCode) => {
    if (isJoinConsultationPath.value && newCode) {
      joinCallWithConsultationShareCode(newCode as string).then(() => {
        if (!callConsultationRequestId.value) {
          router.push({ name: routeNames.CONSULTATIONS })
        } else {
          callFrameSrc.value = createCallFrameUrl()
        }
      })
    }
  }
)

function createCallFrameUrl() {
  if (!showCard.value || isWebexCall.value) {
    return null
  }

  if (!accessToken.value) {
    handleAccessTokenError()
    return null
  }

  const base64AccessToken = btoa(accessToken.value)
  const url = new URL(`video/${roomId.value}?tid=${base64AccessToken}`, getAkBaseUrl())
  url.searchParams.set('lang', locale.value)

  return url.toString()
}

function handleAccessTokenError() {
  addNotice({
    type: NoticeType.ERROR,
    message: t('Failed to join the call, because of invalid access token')
  })
  if (isJoinConsultationPath.value) {
    router.push({ name: routeNames.CONSULTATIONS })
  }
}

function copyShareCodeToClipboard() {
  if (consultationShareCode.value) {
    navigator.clipboard.writeText(consultationShareCode.value)
  } else if (scheduledConsultationShareCode.value) {
    navigator.clipboard.writeText(scheduledConsultationShareCode.value)
  }
}

function handleCallClosed() {
  leaveCall()
  if (isJoinConsultationPath.value || isScheduledCallPath.value) {
    router.push({ name: routeNames.CONSULTATIONS })
  }
}

function handleMessageEvent(msg: MessageEvent<string>) {
  if (isWebexCall.value) return

  if (msg.data === 'CALL_FOUND') {
    isCallFound.value = true
  }

  if (msg.data === 'CALL_CLOSED') {
    handleCallClosed()
    return
  }

  if (msg.data === 'CALL_NOT_FOUND') {
    addNotice({
      type: NoticeType.ERROR,
      message: t('Failed to join the call')
    })

    if (isJoinConsultationPath.value || isScheduledCallPath.value) {
      router.push({ name: routeNames.CONSULTATIONS })
    }
    leaveCall()
    return
  }
}
</script>

<style scoped>
iframe {
  width: 100%;
  height: 750px;
  border: none;
}
</style>
